<!-- eslint-disable vue/no-parsing-error -->
<!-- Auther: Ghufran Ahmed -->

<template>
  <section class="arrange-page">
    <div class="arrange-shell">
      <div class="arrange-backdrop"></div>

      <header class="arrange-hero">
        <div class="arrange-hero__copy">
          <p class="arrange-hero__eyebrow">Custom dashboard builder</p>
          <h1>Select your sections and arrange your widgets</h1>
          <p class="arrange-hero__description">
            Shape the final flow for
            <strong>{{ dashboardDetails.name || 'your dashboard' }}</strong
            >. Turn sections on, drag them into place, and publish a version that feels intentional
            from the first glance to the last chart.
          </p>
        </div>

        <aside class="arrange-hero__panel">
          <span class="arrange-hero__step">Step 5</span>
          <h2>Ready to publish</h2>
          <div class="arrange-stats">
            <div class="arrange-stat">
              <span class="arrange-stat__label">Selected</span>
              <strong class="arrange-stat__value">{{ selectedSectionsCount }}</strong>
            </div>
            <div class="arrange-stat">
              <span class="arrange-stat__label">Available</span>
              <strong class="arrange-stat__value">{{ values.length }}</strong>
            </div>
          </div>
          <p class="arrange-hero__hint">
            Drag cards to reorder the experience. Keep at least one section active before you create
            the dashboard.
          </p>
        </aside>
      </header>

      <div class="arrange-layout">
        <div class="arrange-board">
          <div class="arrange-board__header">
            <div>
              <p class="arrange-board__eyebrow">Section Library</p>
              <h2>Choose and order the layout</h2>
            </div>
            <span class="arrange-board__badge">{{ selectedSectionsCount }} active</span>
          </div>

          <draggable
            v-model="values"
            class="arrange-grid"
            ghost-class="arrange-card--ghost"
            @update="handleListUpdate"
          >
            <transition-group tag="div" name="arrange-card" class="arrange-grid__inner">
              <article
                v-for="(value, index) in values"
                :key="value.fieldName"
                :id="index"
                class="arrange-card"
                :class="{ 'arrange-card--selected': value.selected }"
              >
                <div class="arrange-card__top">
                  <label :for="`dashboard${index}`" class="arrange-card__toggle">
                    <input
                      :id="`dashboard${index}`"
                      v-model="value.selected"
                      type="checkbox"
                      :name="`dashboard${index}`"
                      class="arrange-card__checkbox"
                      @click="selectedComponent($event, value.fieldName)"
                    />
                    <span class="arrange-card__checkmark"></span>
                    <span class="arrange-card__title">{{ value.fieldName }}</span>
                  </label>

                  <div class="arrange-card__drag">
                    <b-icon icon="grip-vertical"></b-icon>
                    <span>Drag</span>
                  </div>
                </div>

                <p class="arrange-card__description">
                  {{ getSectionDescription(value.fieldName) }}
                </p>

                <div class="arrange-card__preview">
                  <img
                    :src="value.fieldImage"
                    :alt="`${value.fieldName} preview`"
                    class="arrange-card__image"
                  />
                </div>
              </article>
            </transition-group>
          </draggable>
        </div>

        <aside class="arrange-summary">
          <div class="summary-card">
            <p class="summary-card__eyebrow">Dashboard Summary</p>
            <h2>{{ dashboardDetails.name || 'Untitled dashboard' }}</h2>
            <p class="summary-card__description">
              {{
                dashboardDetails.description ||
                'Select the sections you want to include and refine the final reading order.'
              }}
            </p>
          </div>

          <div class="summary-card">
            <p class="summary-card__eyebrow">Current Order</p>
            <div v-if="orderedSelectedSections.length" class="summary-list">
              <div
                v-for="(value, index) in orderedSelectedSections"
                :key="`${value.fieldName}-summary`"
                class="summary-list__item"
              >
                <span class="summary-list__index">{{ index + 1 }}</span>
                <span class="summary-list__label">{{ value.fieldName }}</span>
              </div>
            </div>
            <p v-else class="summary-card__empty">
              No section is active yet. Select at least one card to build the dashboard flow.
            </p>
          </div>
        </aside>
      </div>

      <div class="arrange-actions">
        <button class="create_dashboard_btn" @click="approveData">
          {{ $store.getters.editMode ? 'Update' : 'Create' }} dashboard
        </button>

        <b-popover
          ref="popover"
          target="popover-button-event"
          triggers="hover"
          title="Choose visibility"
        >
          <span @click="createPrivateDashboard()" class="choose-visibility-option">
            <b-icon icon="person-fill" style="color: #7952b3"></b-icon>
            Private dashboard</span
          >
          <br />
          <span class="choose-visibility-option" v-b-modal.modal-public-dashboard>
            <b-icon icon="globe" style="color: #7952b3"></b-icon>
            Public dashboard</span
          >
        </b-popover>

        <b-modal id="modal-in-review" title="BootstrapVue" size="lg" hide-footer hide-header>
          <div class="in-review">
            <b-icon icon="exclamation-circle" style="color: #7952b3"></b-icon>
            <br />
            <h2>Your dashboard is currently in review</h2>
            <span>You will be notified when your dashboard is published</span>
            <br />
            <span>you will be directed to your dashboard in 5 seconds</span>
          </div>
        </b-modal>

        <b-modal id="modal-public-dashboard" title="BootstrapVue" size="lg" hide-footer hide-header>
          <div class="modal-form-div">
            <span class="modalHeader1">Create a Public Dashboard</span>
            <br /><br />
            <b-form @submit="onSubmit" @reset="onReset">
              <b-row>
                <b-col>
                  <b-form-group id="input-group-2" label="Your Name:" label-for="input-2">
                    <b-form-input
                      id="input-2"
                      v-model="public_creator.name"
                      placeholder="Full name"
                      class="input"
                      required
                    ></b-form-input> </b-form-group
                ></b-col>

                <b-col>
                  <b-form-group
                    id="input-group-1"
                    label="Email address:"
                    label-for="input-1"
                    required
                  >
                    <b-form-input
                      id="input-1"
                      v-model="public_creator.email"
                      type="email"
                      placeholder="Enter email"
                      class="input"
                      required
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group id="input-group-2" label="Reason:" label-for="input-2">
                <b-form-input
                  id="input-2"
                  v-model="public_creator.Reason"
                  placeholder="Reason"
                  class="input"
                  required
                ></b-form-input>
              </b-form-group>

              <b-form-group id="input-group-2" label="Dashboard name:" label-for="input-2">
                <b-form-input
                  id="input-2"
                  v-model="public_creator.name_of_dashboard"
                  placeholder="Dashboard name"
                  class="input"
                  required
                ></b-form-input>
              </b-form-group>
              <br />
              <b-button type="submit" variant="primary" class="create_dashboard_btn">
                Create Dashboard
              </b-button>
            </b-form>
          </div>
        </b-modal>

        <b-modal id="modal-visibility" title="BootstrapVue" size="lg" hide-footer>
          <b-button v-b-toggle.collapse-private variant="primary">
            Create a private dashboard
          </b-button>
          <b-collapse id="collapse-private" class="mt-2">
            <button @click="createPrivateDashboard()">Private dashboard</button>
          </b-collapse>

          <b-button v-b-toggle.collapse-public variant="primary" hide-header>
            Create a public dashboard
          </b-button>
          <b-collapse id="collapse-public" class="mt-2">
            <b-card>
              <b-form-input
                v-model="public_creator.name"
                placeholder="Full name"
                class="input"
              ></b-form-input>
              <br />
              <b-form-input
                v-model="public_creator.email"
                placeholder="Email address"
              ></b-form-input>
              <br />
              <br />
              <b-form-input v-model="public_creator.Reason" placeholder="Reason"></b-form-input>
              <br />
              <b-form-input
                v-model="public_creator.name_of_dashboard"
                placeholder="Name of Dashboard"
              ></b-form-input>
              <br />
              <button @click="createPublicDashboard()">Public dashboard</button>
            </b-card>
          </b-collapse>
        </b-modal>
      </div>
    </div>
  </section>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import draggable from 'vuedraggable';
import apiServices from '@/modules/data-layer/services/ApiServices';
import resizeConfig from '../utils/resizeConfig';

const SECTION_DEFINITIONS = [
  {
    fieldName: 'Indicator Overview',
    fieldImage: '/img/dashboardPreviewImages/Dashboard.PNG',
  },
  {
    fieldName: 'Zonal Analysis',
    fieldImage: '/img/dashboardPreviewImages/ZonalAnalysis.PNG',
  },
  {
    fieldName: 'Indicator Comparison',
    fieldImage: '/img/dashboardPreviewImages/IndicatorComparision-byPeriod.PNG',
  },
  {
    fieldName: 'Dataset Comparison',
    fieldImage: '/img/dashboardPreviewImages/DataSetComparison.PNG',
  },
  {
    fieldName: 'Multi-source Comparison',
    fieldImage: '/img/dashboardPreviewImages/MultiSourceComparison.PNG',
  },
];

export default {
  components: {
    // eslint-disable-next-line vue/no-unused-components
    draggable,
  },
  data() {
    return {
      orderModified: false,
      values: [],
      url: '',
      public_creator: {
        name: '',
        email: '',
        // organization: '',
        Reason: '',
        name_of_dashboard: '',
        dashboard_details: null,
        embedded_url: null,
        embedded_url_title: null,
        embedded_iframe: null,
        embedded_iframe_title: null,
        embedded_dashboard_description: null,
        is_private: false,
      },
      form: {
        email: '',
        name: '',
        food: null,
        checked: [],
      },
      foods: [{ text: 'Select One', value: null }, 'Carrots', 'Beans', 'Tomatoes', 'Corn'],
      show: true,
    };
  },
  mounted() {
    this.$store.commit('updateStep', 5);
    this.syncValuesFromSections();
    const programArea = this.$store.getters.getprogramArea;
    const dataSource = this.$store.getters.getDataSource;
    const urlTitle = this.$store.getters.getNewEmbedUrlTitle;
    const url = this.$store.getters.getUrl;
    const iframeTitle = this.$store.getters.getNewEmbedIframeTitle;
    const iframe = this.$store.getters.getIframeEmbed;
    const description = this.$store.getters.getNewEmbedDashboardDescription;

    if (programArea != null && dataSource != null) {
      const tempConfig = {
        dashboardDetails: this.$store.getters.dashboardDetails,
        selectionSource: this.$store.getters.activeSelectionSource,
        composedData: programArea,
        surveyArray: dataSource,
        url,
        iframe,
        urlTitle,
        iframeTitle,
        description,
      };
      const stringifyConfig = JSON.stringify(tempConfig);
      localStorage.setItem('storedConfig', stringifyConfig);
    }
  },
  computed: {
    ...mapGetters('AUTH_STORE', ['getUser']),
    // ...mapGetters('CUSTOM_DASHBOARD_STORE', ['dashboardDetails', 'getVisibility']),
    dashboardDetails() {
      return this.$store.getters.dashboardDetails;
    },
    getVisibility() {
      return this.$store.getters.getVisibility;
    },
    getUrl() {
      return this.$store.getters.getEmbedUrl;
    },
    getEmbedIframe() {
      return this.$store.getters.getEmbedIframe;
    },
    arrangedSections() {
      return this.$store.getters.arrangedSections || [];
    },
    selectedSectionsCount() {
      return this.values.filter((value) => value.selected).length;
    },
    orderedSelectedSections() {
      return this.values.filter((value) => value.selected);
    },
  },
  watch: {
    arrangedSections: {
      deep: true,
      handler() {
        if (!this.orderModified) {
          this.syncValuesFromSections();
        }
      },
    },
  },
  methods: {
    ...mapActions('AUTH_STORE', ['SAVE_USER_DASHBOARD']),
    normalizeSectionName(name) {
      return String(name || '')
        .trim()
        .toLowerCase();
    },

    open(url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    async showDashboardCreatedSuccess(url) {
      if (!url) return;

      const result = await this.$swal.fire({
        title: 'Dashboard created successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      if (result.isConfirmed) {
        this.clearEmbedDashboardState();
        this.open(url);
        this.$router.push('/custom');
      }
    },
    clearEmbedDashboardState() {
      this.$store.dispatch('setEmbedUrl', '');
      this.$store.dispatch('setEmbedIframe', '');
      this.$store.dispatch('setNewEmbedUrlTitleAction', '');
      this.$store.dispatch('setNewEmbedIframeTitleAction', '');
      this.$store.dispatch('setEmbedDashboardDescriptionAction', '');

      sessionStorage.removeItem('embedUrl');
      sessionStorage.removeItem('embedIframe');
      sessionStorage.removeItem('embedUrlTitle');
      sessionStorage.removeItem('embedIframeTitle');
      sessionStorage.removeItem('setEmbedUrlTitle');
      sessionStorage.removeItem('setEmbedIframeTitle');
      sessionStorage.removeItem('setEmbedDashboardDesc');
      localStorage.removeItem('activeTab');
    },
    buildDashboardLink(dashboard) {
      if (!dashboard?.link) return '';
      const separator = dashboard.link.endsWith('/') ? '' : '/';
      return dashboard.id ? `${dashboard.link}${separator}${dashboard.id}` : dashboard.link;
    },
    getDashboardFromResponse(response) {
      return response?.data?.data || response?.data || null;
    },

    findSectionConfig(fieldName) {
      const normalizedFieldName = this.normalizeSectionName(fieldName);

      return this.arrangedSections.find(
        (section) => this.normalizeSectionName(section.name) === normalizedFieldName,
      );
    },
    syncValuesFromSections() {
      this.values = SECTION_DEFINITIONS.map((sectionDefinition) => {
        const matchingSection = this.findSectionConfig(sectionDefinition.fieldName);

        return {
          ...sectionDefinition,
          selected: matchingSection ? matchingSection.isShow : false,
        };
      });
    },
    getSectionDescription(fieldName) {
      const descriptions = {
        'Indicator Overview':
          'Lead with the signal. This section gives the dashboard a clear opening view of the most important metrics and storylines.',
        'Zonal Analysis':
          'Highlight geographic patterns quickly so viewers can compare states, zones, or locations at a glance.',
        'Indicator Comparison':
          'Place key indicators side by side to surface relationships, gaps, and movement across time or contexts.',
        'Dataset Comparison':
          'Contrast datasets to help viewers understand where sources align, diverge, or add nuance.',
        'Multi-source Comparison':
          'Combine perspectives from multiple sources to create a richer and more credible narrative.',
      };

      return descriptions[fieldName] || 'Enable this section to expand the dashboard narrative.';
    },
    async onSubmit(event) {
      event.preventDefault();
      await this.createPublicDashboard();
    },
    onReset(event) {
      event.preventDefault();
      // Reset our form values
      this.public_creator.name = '';
      this.public_creator.email = '';
      // this.public_creator.organization = '';
      this.public_creator.Reason = '';
      this.public_creator.name_of_dashboard = '';
    },
    changeVisibility(status) {
      if (status === 'private') {
        this.$store.dispatch('setVisibility', 'private');
      }

      if (status === 'public') {
        this.$store.dispatch('setVisibility', 'public');
      }
    },

    areAllSelectedFalse(data) {
      return data.every((item) => item.selected === false);
    },

    removeNullFields(obj) {
      return Object.fromEntries(
        // eslint-disable-next-line no-unused-vars
        Object.entries(obj).filter(([_, value]) => value != null && value !== ''),
      );
    },

    async createPublicDashboard() {
      // send the request to create a public dashboard
      this.public_creator.dashboard_details = await this.$store.getters.dashboardDetails;
      await this.$store.dispatch('setDashboardRequest', this.public_creator);
      // hide the 'modal-public-dashboard'
      await this.$bvModal.hide('modal-public-dashboard');
      // show the 'modal-in-review'
      await this.$bvModal.show('modal-in-review');

      // Wait for 10 seconds using setTimeout
      setTimeout(() => {
        // After waiting for 10 seconds, call the approveData() function
        this.approveData();
      }, 5000); // 10000 milliseconds = 10 seconds
    },
    handleListUpdate() {
      this.orderModified = true;
    },
    reorderStoreConfig() {
      const mapping = {};
      this.values.forEach((item, index) => {
        mapping[item.fieldName] = index;
      });
      const storeArray = [...this.$store.getters.arrangedSections];
      return storeArray.sort((a, b) => mapping[a.name] - mapping[b.name]);
    },
    async createPrivateDashboard() {
      // send the request to create a public daashboard

      // change the visibility
      await this.changeVisibility('private');
      // create the dashboard using the approveData() function
      await this.approveData();
    },

    // Below function is excuted when approve data button is clicked

    async approveData() {
      if (this.areAllSelectedFalse(this.values)) {
        await this.$swal.fire({
          title: 'No Section Selected',
          text: 'Warning: You need to select at least a section',
        });
        return;
      }

      const storedConfig = localStorage.getItem('storedConfig');
      const parsedConfig = storedConfig ? JSON.parse(storedConfig) : {};
      const embeddedUrl = this.$store.getters.getEmbedUrl || parsedConfig.url || null;
      const embeddedIframe = this.$store.getters.getEmbedIframe || parsedConfig.iframe || null;
      const embeddedUrlTitle
        = this.$store.getters.getNewEmbedUrlTitle || parsedConfig.urlTitle || null;
      const embeddedIframeTitle
        = this.$store.getters.getNewEmbedIframeTitle || parsedConfig.iframeTitle || null;
      const embeddedDashboardDescription
        = this.$store.getters.getNewEmbedDashboardDescription || parsedConfig.description || null;
      if (!this.dashboardDetails.name) {
        // eslint-disable-next-line no-alert
        this.$swal('Dashboard name not provided');
        return;
      }
      let arrangedSections = this.$store.getters.arrangedSections;
      if (this.orderModified) {
        arrangedSections = this.reorderStoreConfig();
      }
      const config = resizeConfig({
        dashboardDetails: this.$store.getters.dashboardDetails,
        selectionSource: this.$store.getters.activeSelectionSource,
        composedData: this.$store.getters.getprogramArea || parsedConfig.composedData,
        surveyArray: this.$store.getters.getDataSource || parsedConfig.surveyArray,
        sectionsArray: arrangedSections,
      });
      // if the dashboard is public, run these functions
      if (this.getVisibility === 'public') {
        const id = `${Date.now()}${this.getUser.id}`;
        this.public_creator.id = id;
        this.public_creator.name = this.getUser.username;
        this.public_creator.email = this.getUser.email;
        this.public_creator.description = this.dashboardDetails.description;
        this.public_creator.Reason = this.dashboardDetails.reason;
        this.public_creator.category = this.dashboardDetails.category;
        this.public_creator.config = JSON.stringify(config);
        this.public_creator.created = new Date();
        this.public_creator.name_of_dashboard = this.dashboardDetails.name;
        this.public_creator.link = `${window.location.origin}/custom/public/`;
        this.public_creator.embedded_url = embeddedUrl;
        this.public_creator.embedded_url_title = embeddedUrlTitle;
        this.public_creator.embedded_iframe = embeddedIframe;
        this.public_creator.embedded_iframe_title = embeddedIframeTitle;
        this.public_creator.embedded_dashboard_description = embeddedDashboardDescription;

        if (this.areAllSelectedFalse(this.values)) {
          await this.$swal.fire({
            title: 'No Section Selected',
            text: 'Warning: You need to select at least a section',
          });
        } else {
          const cleanedData = this.removeNullFields(this.public_creator);
          console.log('cleanedData', cleanedData);
          // const res = await this.$store.dispatch('setDashboardRequest', this.public_creator);

          console.log(cleanedData);
          try {
            const res = await apiServices.saveCustomDashboard(cleanedData);

            if (res) {
              this.$store.dispatch('customDashboard', true);
              const savedDashboard = this.getDashboardFromResponse(res);
              const publicDashboardLink = this.buildDashboardLink(savedDashboard);

              if (publicDashboardLink) {
                await this.showDashboardCreatedSuccess(publicDashboardLink);
              }
            }
          } catch (error) {
            this.$swal.fire({
              title: 'Error',
              text: `${error.response.data.message}`,
              icon: 'error',
            });
          }
        }
      } else {
        // Save Private Dashboard
        const data = {
          name: this.getUser.username,
          email: this.getUser.email,
          category: this.dashboardDetails.category,
          reason: this.dashboardDetails.reason,
          description: this.dashboardDetails.description,
          name_of_dashboard: this.dashboardDetails.name,
          organization: 'string',
          config: JSON.stringify(config),
          link: `${window.location.origin}/custom/public/`,
          embedded_url: embeddedUrl,
          embedded_url_title: embeddedUrlTitle,
          embedded_iframe: embeddedIframe,
          embedded_iframe_title: embeddedIframeTitle,
          is_confirmed: false,
          is_private: this.$store.getters.getVisibility === 'private',
          embedded_dashboard_description: embeddedDashboardDescription,
        };

        try {
          const cleanedData = this.removeNullFields(data);

          const res = await this.SAVE_USER_DASHBOARD(cleanedData);

          this.$store.dispatch('customDashboard', true);
          console.log(res, 'RESPONSE');
          const savedDashboard = this.getDashboardFromResponse(res);
          const dashboardLink = this.buildDashboardLink(savedDashboard);

          if (dashboardLink) {
            await this.showDashboardCreatedSuccess(dashboardLink);
          }
        } catch (error) {
          this.$swal.fire({
            title: 'Error',
            text: `${error.response.data.message}`,
            icon: 'error',
          });
        }
      }
    },

    // PRESELECTION OF Dashboard widgets
    selectedComponent(e, fieldName) {
      const payload = {
        checked: e.target.checked,
        checkedField: fieldName,
      };
      this.$store.dispatch('dynamicSection', payload);
      if (fieldName === 'Indicator Overview') {
        this.$store.state.MSDAT_STORE.indicatorComparision = e.target.checked;
      }
      if (fieldName === 'Zonal Analysis') {
        this.$store.state.MSDAT_STORE.zonalAnalysis = e.target.checked;
      }
      if (fieldName === 'Indicator Comparison') {
        this.$store.state.MSDAT_STORE.indicatorComparsionByPeriod = e.target.checked;
      }

      if (fieldName === 'Multi-source Comparison') {
        this.$store.state.MSDAT_STORE.multisourceComparison = e.target.checked;
      }
      if (fieldName === 'Dataset Comparison') {
        this.$store.state.MSDAT_STORE.datasetComperision = e.target.checked;
      }
    },
    isSelected(value) {
      return value.selected;
    },
  },
};
</script>

<style scoped>
.arrange-page {
  min-height: 100%;
  padding: 28px 0 56px;
  background: radial-gradient(circle at top left, rgba(34, 126, 141, 0.16), transparent 24%),
    radial-gradient(circle at bottom right, rgba(219, 233, 221, 0.72), transparent 36%),
    linear-gradient(150deg, #f5faf9 0%, #fdf8f0 52%, #f2f7fb 100%);
}

.arrange-shell {
  position: relative;
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
}

.arrange-backdrop {
  position: absolute;
  inset: 42px auto auto 50%;
  width: min(720px, 82%);
  height: 420px;
  transform: translateX(-50%);
  border-radius: 999px;
  background: radial-gradient(circle, rgba(63, 137, 148, 0.2) 0%, rgba(63, 137, 148, 0) 68%);
  filter: blur(10px);
  pointer-events: none;
}

.arrange-hero {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(300px, 0.8fr);
  gap: 1.5rem;
  align-items: start;
  margin-bottom: 1.5rem;
}

.arrange-hero__copy,
.arrange-hero__panel,
.arrange-board,
.summary-card,
.arrange-actions {
  border: 1px solid rgba(208, 224, 227, 0.9);
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 40px rgba(17, 46, 53, 0.08);
  backdrop-filter: blur(14px);
}

.arrange-hero__copy {
  padding: 1.75rem 1.9rem;
  border-radius: 30px;
}

.arrange-hero__eyebrow,
.arrange-board__eyebrow,
.summary-card__eyebrow {
  margin-bottom: 0.35rem;
  color: #3f8994;
  font-family: 'Work Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.arrange-hero__copy h1 {
  margin-bottom: 0.8rem;
  color: #173c44;
  font-family: 'Work Sans', sans-serif;
  font-size: clamp(2rem, 3vw, 3.15rem);
  font-weight: 700;
  line-height: 1.05;
}

.arrange-hero__description {
  max-width: 720px;
  margin-bottom: 0;
  color: #587076;
  font-family: 'Work Sans', sans-serif;
  font-size: 15px;
  line-height: 1.75;
}

.arrange-hero__panel {
  padding: 1.5rem;
  border-radius: 26px;
}

.arrange-hero__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  background: #173c44;
  color: #ffffff;
  font-family: 'Work Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.arrange-hero__panel h2,
.arrange-board__header h2,
.summary-card h2 {
  margin: 0.9rem 0 0.65rem;
  color: #173c44;
  font-family: 'Work Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
}

.arrange-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin: 1rem 0 1.15rem;
}

.arrange-stat {
  padding: 0.95rem 1rem;
  border: 1px solid #d8e8eb;
  border-radius: 18px;
  background: linear-gradient(180deg, #fbfefd 0%, #f3f9f8 100%);
}

.arrange-stat__label {
  display: block;
  margin-bottom: 0.25rem;
  color: #718287;
  font-family: 'Work Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.arrange-stat__value {
  color: #035c6e;
  font-family: 'Work Sans', sans-serif;
  font-size: 28px;
  font-weight: 700;
}

.arrange-hero__hint,
.summary-card__description,
.summary-card__empty {
  margin-bottom: 0;
  color: #587076;
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  line-height: 1.7;
}

.arrange-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(280px, 0.85fr);
  gap: 1.5rem;
  align-items: start;
}

.arrange-board {
  padding: 1.4rem;
  border-radius: 28px;
}

.arrange-board__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.2rem;
}

.arrange-board__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  background: #ecf6f7;
  color: #035c6e;
  font-family: 'Work Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
}

.arrange-grid {
  width: 100%;
}

.arrange-grid__inner {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.arrange-card {
  padding: 1rem;
  border: 1px solid #ddebed;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(246, 251, 250, 0.92) 100%);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.arrange-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 28px rgba(17, 46, 53, 0.08);
}

.arrange-card--selected {
  border-color: #94cfd4;
  box-shadow: 0 18px 28px rgba(3, 92, 110, 0.08);
}

.arrange-card--ghost {
  opacity: 0.55;
}

.arrange-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.8rem;
}

.arrange-card__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 0;
  cursor: pointer;
}

.arrange-card__checkbox {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.arrange-card__checkmark {
  position: relative;
  width: 22px;
  height: 22px;
  border: 1px solid #c6dde0;
  border-radius: 7px;
  background: #ffffff;
  flex-shrink: 0;
}

.arrange-card__checkbox:checked + .arrange-card__checkmark {
  border-color: #035c6e;
  background: #035c6e;
}

.arrange-card__checkbox:checked + .arrange-card__checkmark::after {
  content: '';
  position: absolute;
  top: 4px;
  left: 7px;
  width: 5px;
  height: 10px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.arrange-card__title {
  color: #173c44;
  font-family: 'Work Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
}

.arrange-card__drag {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.65rem;
  border-radius: 999px;
  background: #f1f6f7;
  color: #6b8086;
  font-family: 'Work Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: move;
}

.arrange-card__description {
  min-height: 72px;
  margin-bottom: 0.95rem;
  color: #5e7278;
  font-family: 'Work Sans', sans-serif;
  font-size: 13px;
  line-height: 1.7;
}

.arrange-card__preview {
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid #e5eff0;
  background: #eff5f6;
}

.arrange-card__image {
  display: block;
  width: 100%;
  height: 220px;
  object-fit: cover;
}

.arrange-summary {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.summary-card {
  padding: 1.35rem;
  border-radius: 24px;
}

.summary-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.95rem;
}

.summary-list__item {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.85rem 0.95rem;
  border: 1px solid #ddebed;
  border-radius: 16px;
  background: #f9fcfc;
}

.summary-list__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #173c44;
  color: #ffffff;
  font-family: 'Work Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
}

.summary-list__label {
  color: #173c44;
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
}

.arrange-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem 1.2rem;
  border-radius: 22px;
}

.create_dashboard_btn {
  min-width: 220px;
  height: 48px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #035c6e 0%, #2f8c97 100%);
  box-shadow: 0 14px 24px rgba(3, 92, 110, 0.2);
  color: #ffffff;
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}

.create_dashboard_btn:hover {
  background: linear-gradient(135deg, #0b7488 0%, #3b9eaa 100%);
  box-shadow: 0 18px 30px rgba(3, 92, 110, 0.28);
  color: #ffffff;
  transform: translateY(-1px);
}

.modalHeader1 {
  color: #202020;
  font: normal normal bold 35px/47px DM Sans;
  letter-spacing: 0;
  opacity: 1;
}

.modal-form-div {
  padding: 20px;
}

.input {
  background: #eaeaea 0 0 no-repeat padding-box;
  border: none;
  opacity: 1;
  padding: 20px;
}

.in-review {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.choose-visibility-option {
  cursor: pointer;
}

.choose-visibility-option:hover {
  opacity: 0.7;
}

label {
  margin-bottom: 0;
}

@media (max-width: 1180px) {
  .arrange-hero,
  .arrange-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 991px) {
  .arrange-grid__inner {
    grid-template-columns: 1fr;
  }

  .arrange-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .create_dashboard_btn {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .arrange-page {
    padding: 20px 0 40px;
  }

  .arrange-shell {
    width: min(100%, calc(100% - 20px));
  }

  .arrange-hero__copy,
  .arrange-hero__panel,
  .arrange-board,
  .summary-card,
  .arrange-actions {
    border-radius: 20px;
  }

  .arrange-hero__copy h1 {
    font-size: 30px;
  }

  .arrange-card__image {
    height: 180px;
  }
}
</style>
